import { Alert, Button, Snackbar, Tabs } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import "../styles/popups.scss";
import { ConfigurationPanel } from "./ConfigurationPanel";
import { DescriptionsPanel } from "./DescriptionsPanel";
import { useCallback, useContext, useEffect, useState } from "react";
import { PreferencesContext } from "../providers/PreferencesProvider";
import type { DisplayAccountTreeNode } from "./DistributionListPanel";
import { DistributionListPanel } from "./DistributionListPanel";
import { StatePanel } from "./StatePanel";
import { useWriteAPI } from "@dealer-center/ddc-pages-incentives-manager-fetch";
import type { decodedJwtAndPermissions } from "@dealer-center/ddc-api-user-interface";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import { useOutletContext } from "react-router-dom";
import { hasObjectChanged } from "../utils/utils";
import { useQueryClient } from "@tanstack/react-query";

interface PreferencesPopupProps {
  onCancel?: () => void;
  onSave?: () => void;
}

export const PreferencesPopup = ({
  onCancel,
  onSave
}: PreferencesPopupProps) => {
  const { preferences } = useContext(PreferencesContext);
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, boolean>
  >({});
  const [selectedTab, setSelectedTab] = useState<string>(
    Locale.getText("CONFIGURATION") as string
  );
  const [mutableConfiguration, setMutableConfiguration] = useState(() =>
    JSON.parse(JSON.stringify(preferences.accountConfiguration))
  );
  const [mutableDescriptions, setMutableDescriptions] = useState(() =>
    JSON.parse(JSON.stringify(preferences.contentDefaults))
  );
  const [mutableStateDisclaimers, setMutableStateDisclaimers] = useState(() =>
    JSON.parse(JSON.stringify(preferences.stateDisclaimer))
  );
  const [mutableGroups, setMutableGroups] = useState(() =>
    JSON.parse(JSON.stringify(preferences.groups || []))
  );
  const { ccUserInfo } = useOutletContext<{
    ccUserInfo: ControlcenterAPIInterfaceExternal;
    userEntity: decodedJwtAndPermissions;
  }>();
  const setMutateError = (error: Error) => {
    const mess = error.message.toLowerCase();
    const matchLabel = mess.includes("account")
      ? "ACCOUNT_CONFIGURATION"
      : mess.includes("distribution")
        ? "DISTRIBUTION_LISTS"
        : "CREATION_DEFAULTS";
    setErrors(prevErrors => {
      if (!prevErrors.includes(Locale.getText(matchLabel) as string)) {
        return [...prevErrors, Locale.getText(matchLabel) as string];
      }
      return prevErrors;
    });
  };

  const changeError = useCallback((id: string, status: boolean) => {
    setValidationErrors(prev => ({ ...prev, [id]: status }));
  }, []);

  const hasValidationErrors = () =>
    Object.values(validationErrors).some((value: boolean) => value);

  const getValidationErrorMessage = () => {
    const hasIncentiveTypeErrors =
      validationErrors["pref-description-prohibited"] === true ||
      validationErrors["pref-disclaimer-prohibited"] === true;
    const hasStateErrors =
      validationErrors["state-disclaimer-prohibited"] === true;

    if (hasIncentiveTypeErrors && hasStateErrors) {
      return Locale.getText("FIX_STATE_AND_INCENTIVE_FIELD_ERRORS");
    } else if (hasIncentiveTypeErrors) {
      return Locale.getText("FIX_INCENTIVE_FIELD_ERRORS");
    } else if (hasStateErrors) {
      return Locale.getText("FIX_STATE_FIELD_ERRORS");
    }
    return "";
  };

  useEffect(() => {
    if (
      mutableConfiguration &&
      mutableDescriptions &&
      mutableStateDisclaimers &&
      mutableGroups
    ) {
      setHasChanges(
        hasObjectChanged(
          preferences.accountConfiguration,
          mutableConfiguration
        ) ||
          hasObjectChanged(preferences.contentDefaults, mutableDescriptions) ||
          hasObjectChanged(
            preferences.stateDisclaimer,
            mutableStateDisclaimers
          ) ||
          hasObjectChanged(preferences.groups, mutableGroups)
      );
    }
  }, [
    mutableConfiguration,
    mutableDescriptions,
    mutableStateDisclaimers,
    mutableGroups,
    preferences.accountConfiguration,
    preferences.contentDefaults,
    preferences.stateDisclaimer,
    preferences.groups
  ]);

  const savePreferences = async () => {
    if (hasChanges && !errors.length && !hasValidationErrors()) {
      const savePromises: Promise<void>[] = [];
      let hasGroupChanges = false;

      if (
        hasObjectChanged(preferences.accountConfiguration, mutableConfiguration)
      ) {
        savePromises.push(
          updateConfigurationMutation.mutateAsync({
            body: mutableConfiguration as object
          })
        );
      }
      if (hasObjectChanged(preferences.contentDefaults, mutableDescriptions)) {
        savePromises.push(
          updateDescriptionMutation.mutateAsync({
            body: mutableDescriptions as object
          })
        );
      }
      if (
        hasObjectChanged(preferences.stateDisclaimer, mutableStateDisclaimers)
      ) {
        savePromises.push(
          updateDisclaimerMutation.mutateAsync({
            body: mutableStateDisclaimers as object
          })
        );
      }
      for (const group of mutableGroups) {
        if (
          hasObjectChanged(
            preferences.groups?.[mutableGroups.indexOf(group)] ?? [],
            group
          )
        ) {
          hasGroupChanges = true;
          savePromises.push(
            updateGroupMutationBatch.mutateAsync({
              body: {
                group: { ...group }
              },
              resourceId: group.id
            })
          );
        }
      }
      let saveResults: any[] = [];
      try {
        saveResults = await Promise.allSettled(savePromises);
        if (hasGroupChanges) {
          await queryClient.invalidateQueries({
            queryKey: ["ddc-pages-incentives-manager", "distribution-lists"]
          });
        }
      } catch (error) {
        console.warn(error.message); // eslint-disable-line no-console
      } finally {
        setIsSaving(false);
        let isClosePopup = true;
        saveResults.forEach(entry => {
          if (entry.status === "rejected") {
            setMutateError(entry.reason);
            isClosePopup = false;
          }
        });
        isClosePopup && onSave && onSave();
      }
    }
  };
  const restorePreferences = () => {
    if (selectedTab === Locale.getText("CONFIGURATION")) {
      setMutableConfiguration(
        JSON.parse(JSON.stringify(preferences.accountConfiguration))
      );
    }
    if (selectedTab === Locale.getText("CREATION_DEFAULTS")) {
      setMutableDescriptions(
        JSON.parse(JSON.stringify(preferences.contentDefaults))
      );
      setMutableStateDisclaimers(
        JSON.parse(JSON.stringify(preferences.stateDisclaimer))
      );
    }
    if (selectedTab === Locale.getText("DISTRUBUTION_LIST")) {
      setMutableGroups(JSON.parse(JSON.stringify(preferences.groups || [])));
    }
  };

  const updateConfigurationMutation = useWriteAPI({
    resource: "update-account-configuration",
    asAccount: ccUserInfo.accountId,
    asUser: ccUserInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    afterSuccess: "account-configuration"
  });
  const updateDescriptionMutation = useWriteAPI({
    resource: "update-incentive-content-defaults",
    asAccount: ccUserInfo.accountId,
    asUser: ccUserInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    afterSuccess: "incentive-content-defaults"
  });
  const updateDisclaimerMutation = useWriteAPI({
    resource: "update-state-disclaimers",
    asAccount: ccUserInfo.accountId,
    asUser: ccUserInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    afterSuccess: "state-disclaimers"
  });
  const updateGroupMutation = useWriteAPI({
    resource: "save-distribution-lists-group",
    asAccount: ccUserInfo.accountId,
    asUser: ccUserInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    afterSuccess: "distribution-lists"
  });
  const updateGroupMutationBatch = useWriteAPI({
    resource: "save-distribution-lists-group",
    asAccount: ccUserInfo.accountId,
    asUser: ccUserInfo.userId,
    locale: Locale.getCurrent().replace("-", "_")
  });
  const getTabs = useCallback(() => {
    return [
      {
        component: (
          <div className="popup-content">
            <ConfigurationPanel
              configuration={mutableConfiguration || undefined}
              onConfigurationChange={config => {
                setMutableConfiguration(config);
              }}
            />
          </div>
        ),
        label: Locale.getText("CONFIGURATION"),
        value: Locale.getText("CONFIGURATION"),
        active: selectedTab === Locale.getText("CONFIGURATION")
      },
      {
        component: (
          <div className="popup-content">
            <DescriptionsPanel
              changeError={changeError}
              descriptions={mutableDescriptions || undefined}
              onDescriptionsChanged={descriptions => {
                setMutableDescriptions(descriptions);
              }}
            />
            <StatePanel
              changeError={changeError}
              stateDisclaimers={mutableStateDisclaimers || undefined}
              states={preferences.filterData?.options.states || []}
              onDisclaimersChanged={disclaimers => {
                setMutableStateDisclaimers(disclaimers);
              }}
            />
          </div>
        ),
        label: Locale.getText("CREATION_DEFAULTS"),
        value: Locale.getText("CREATION_DEFAULTS"),
        active: selectedTab === Locale.getText("CREATION_DEFAULTS")
      },
      {
        component:
          preferences.accountTree &&
          preferences.filterData &&
          preferences.groups ? (
            <div className="popup-content">
              <DistributionListPanel
                accountTree={preferences.accountTree as DisplayAccountTreeNode}
                filterData={preferences.filterData}
                groups={mutableGroups}
                onGroupsChanged={groups => {
                  setMutableGroups(groups);
                }}
              />
            </div>
          ) : (
            <div className="distribution-list-error">
              {Locale.getText("ERROR")}
            </div>
          ),
        label: Locale.getText("DISTRUBUTION_LIST"),
        value: Locale.getText("DISTRUBUTION_LIST"),
        active: selectedTab === Locale.getText("DISTRUBUTION_LIST")
      }
    ];
  }, [
    selectedTab,
    mutableConfiguration,
    mutableDescriptions,
    mutableStateDisclaimers,
    mutableGroups,
    preferences.accountTree,
    preferences.filterData,
    preferences.groups
  ]);

  if (preferences.isLoading) {
    return <div className="loading-spinner">{Locale.getText("LOADING")}</div>;
  }
  if (preferences.accountConfiguration == null) {
    return (
      <div className="no-access-panel">
        <Alert type="error">
          {Locale.getText("NO_PREFS_ACCESS") as string}
        </Alert>
      </div>
    );
  }
  return (
    <>
      <Tabs
        tabs={getTabs()}
        onActivate={(val: string) => {
          setSelectedTab(val);
        }}
      />
      <>
        <div
          className={`popup-actions ${
            errors.length > 0 || hasValidationErrors() ? "has-errors" : ""
          }`}
        >
          {(errors.length > 0 || hasValidationErrors()) && (
            <div className="popup-errors">
              {errors.length > 0 &&
                `${Locale.getText("SAVE_ERRORS") as string}: ${errors.join(", ")}`}
              {errors.length > 0 && hasValidationErrors() && " | "}
              {hasValidationErrors() && getValidationErrorMessage()}
            </div>
          )}
          <Button
            analyticsConfiguration={{
              eventName: "preferences-cancel",
              eventProperties: undefined
            }}
            buttonStyle="tertiary"
            disabled={isSaving}
            onClick={() => {
              restorePreferences();
              if (onCancel) {
                onCancel();
              }
            }}
          >
            {Locale.getText("CANCEL")}
          </Button>
          <Button
            analyticsConfiguration={{
              eventName: "preferences-save",
              eventProperties: undefined
            }}
            buttonStyle="primary"
            disabled={
              errors.length > 0 ||
              !hasChanges ||
              isSaving ||
              hasValidationErrors()
            }
            isLoading={isSaving}
            onClick={() => {
              setIsSaving(true);
              savePreferences();
            }}
          >
            {Locale.getText("SAVE")}
          </Button>
        </div>

        <Snackbar
          message={Locale.getText("ERROR_SAVING_PREFERENCES") as string}
          position="bottom-center"
          show={errors.length > 0}
          showCloseButton={false}
          type="error"
        />
      </>
    </>
  );
};
